/**
 * Support Launcher — THE user-facing entry point for support. Replaces the
 * old ContactHQ modal, unifying the previously scattered intakes:
 *
 *   Help          — self-service search + contextual topics (deflection first)
 *   Report        — annotate the actual screen via Review Mode, or a ticket form
 *   Question      — support ticket (the old ContactHQ form, with auto context)
 *   Idea          — consultantFeedback suggestion
 *
 * Opened from the Layout header "Get Support" button (when the screenReview flag is off) and My Tickets.
 */
import React from 'react';
import {
  BookOpen,
  ClipboardEdit,
  Clock,
  HelpCircle,
  Lightbulb,
  Mail,
  MessageCircleQuestion,
  Phone,
  X,
} from 'lucide-react';
import { useFeatureFlags } from '../../hooks/useFeatureFlags';
import { TicketType } from '../../types/ticket.types';
import { HelpTab } from './launcher/HelpTab';
import { TicketFormTab } from './launcher/TicketFormTab';
import { IdeaTab } from './launcher/IdeaTab';

import { openGetSupport } from '../../utils/supportEvents';

type LauncherTab = 'help' | 'report' | 'question' | 'idea';

interface Props {
  onClose: () => void;
  initialTab?: LauncherTab;
}

export const SupportLauncher: React.FC<Props> = ({ onClose, initialTab = 'help' }) => {
  const [tab, setTab] = React.useState<LauncherTab>(initialTab);
  const { isEnabled } = useFeatureFlags();
  const reviewModeAvailable = isEnabled('screenReview');

  React.useEffect(() => {
    const onKey = (e: KeyboardEvent): void => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [onClose]);

  const tabButton = (t: LauncherTab, icon: React.ElementType, label: string): React.ReactElement => {
    const Icon = icon;
    return (
      <button
        key={t}
        type="button"
        onClick={() => setTab(t)}
        className={`flex items-center gap-1.5 rounded-lg px-3 py-1.5 text-sm font-medium transition-colors ${
          tab === t ? 'bg-primary text-white' : 'text-neutral-600 hover:bg-neutral-100'
        }`}
      >
        <Icon className="h-4 w-4" />
        {label}
      </button>
    );
  };

  return (
    <div className="fixed inset-0 z-[10100] flex items-center justify-center bg-black/50 p-4 backdrop-blur-sm">
      <div className="flex max-h-[85vh] w-full max-w-2xl flex-col overflow-hidden rounded-xl bg-white shadow-xl">
        {/* Header */}
        <div className="flex items-center justify-between border-b border-neutral-100 px-4 py-3">
          <h2 className="flex items-center gap-2 text-base font-semibold text-neutral-900">
            <HelpCircle className="h-5 w-5 text-primary" />
            Help & Support
          </h2>
          <button
            type="button"
            onClick={onClose}
            aria-label="Close"
            className="rounded-full p-1.5 text-neutral-400 hover:bg-neutral-100 hover:text-neutral-700"
          >
            <X className="h-4 w-4" />
          </button>
        </div>

        {/* Tabs */}
        <div className="flex flex-wrap gap-1 border-b border-neutral-100 px-3 py-2">
          {tabButton('help', BookOpen, 'Help')}
          {tabButton('report', ClipboardEdit, 'Report a problem')}
          {tabButton('question', MessageCircleQuestion, 'Ask a question')}
          {tabButton('idea', Lightbulb, 'Suggest an idea')}
        </div>

        {/* Body */}
        <div className="min-h-0 flex-1 overflow-y-auto p-4">
          {tab === 'help' && <HelpTab />}

          {tab === 'report' && (
            <div className="space-y-4">
              {reviewModeAvailable && (
                <button
                  type="button"
                  onClick={() => {
                    onClose();
                    openGetSupport();
                  }}
                  className="flex w-full items-start gap-3 rounded-xl border-2 border-rose-200 bg-rose-50 p-4 text-left transition-colors hover:border-rose-400"
                >
                  <ClipboardEdit className="mt-0.5 h-5 w-5 shrink-0 text-rose-600" />
                  <span>
                    <span className="block text-sm font-semibold text-neutral-900">
                      Mark it up on the screen (best)
                    </span>
                    <span className="block text-xs text-neutral-600">
                      Draw directly on the problem — we capture a screenshot, the page, and what
                      you point at, so support sees exactly what you see.
                    </span>
                  </span>
                </button>
              )}
              <div>
                <p className="mb-2 text-xs font-medium uppercase tracking-wide text-neutral-400">
                  {reviewModeAvailable ? 'Or describe it as a ticket' : 'Describe the problem'}
                </p>
                <TicketFormTab initialType={TicketType.SOFTWARE} />
              </div>
            </div>
          )}

          {tab === 'question' && <TicketFormTab />}

          {tab === 'idea' && <IdeaTab />}
        </div>

        {/* Footer — HQ contact details (carried over from ContactHQ) */}
        <div className="flex flex-wrap items-center justify-center gap-x-5 gap-y-1 border-t border-neutral-100 bg-neutral-50 px-4 py-2 text-xs text-neutral-500">
          <a href="tel:0218720028" className="flex items-center gap-1 hover:text-primary">
            <Phone className="h-3 w-3" /> 021 872 0028
          </a>
          <a href="mailto:support@propinvest.co.za" className="flex items-center gap-1 hover:text-primary">
            <Mail className="h-3 w-3" /> support@propinvest.co.za
          </a>
          <span className="flex items-center gap-1">
            <Clock className="h-3 w-3" /> Mon–Fri 8:00–17:00
          </span>
        </div>
      </div>
    </div>
  );
};

export default SupportLauncher;
